import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 0x40
const CHANNEL_OFFSET = 0x10
const CHANNEL_SIZE = 0x10
const SKIP_FLAGS_OFFSET = 0x17e

type RT22Range = readonly [start: number, end: number, blockSize: number]

export type RT22Profile = {
  protocol: 'rt22'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 16
  nameLength: 0
  powerLevels: readonly ['Low', 'High']
  memorySize: number
  ranges: readonly RT22Range[]
  fileIds: readonly (readonly number[])[]
}

const RT22_FILE_IDS: readonly (readonly number[])[] = [
  [0x50, 0x33, 0x32, 0x30, 0x37, 0x33],
  [0x50, 0x33, 0x00, 0x00, 0x00, 0x33],
  [0x50, 0x33, 0x32, 0x30, 0x37, 0x21],
]
const RT22_RANGES: readonly RT22Range[] = [
  [0x0000, 0x0180, 0x10],
  [0x01b8, 0x01f8, 0x10],
  [0x01f8, 0x0200, 0x08],
  [0x0200, 0x0340, 0x10],
]

export const RT22_PROFILES: readonly RT22Profile[] = [
  {
    protocol: 'rt22',
    id: 'retevis-rt22',
    vendor: 'Retevis',
    model: 'RT22',
    summary: 'Sixteen-channel UHF business radio with CTCSS and DCS support.',
    tags: ['USB', 'Read + write', '16 channels', 'No channel names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    memorySize: 0x0400,
    ranges: RT22_RANGES,
    fileIds: RT22_FILE_IDS,
  },
  {
    protocol: 'rt22',
    id: 'retevis-rt22frs',
    vendor: 'Retevis',
    model: 'RT22FRS',
    summary: 'FRS variant of the compact RT22 family.',
    tags: ['USB', 'Read + write', 'FRS'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    memorySize: 0x0400,
    ranges: RT22_RANGES,
    fileIds: RT22_FILE_IDS,
  },
  {
    protocol: 'rt22',
    id: 'retevis-rt622',
    vendor: 'Retevis',
    model: 'RT622',
    summary: 'RT22-compatible business radio with an additional identity variant.',
    tags: ['USB', 'Read + write', '16 channels'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    memorySize: 0x0400,
    ranges: RT22_RANGES,
    fileIds: [...RT22_FILE_IDS, [0xff, 0xff, 0xf8, 0xff]],
  },
  {
    protocol: 'rt22',
    id: 'wln-kd-c1',
    vendor: 'WLN',
    model: 'KD-C1',
    summary: 'Compact WLN radio using the RT22 programming protocol.',
    tags: ['USB', 'Read + write', '16 channels'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    memorySize: 0x0400,
    ranges: RT22_RANGES,
    fileIds: RT22_FILE_IDS,
  },
  {
    protocol: 'rt22',
    id: 'zastone-zt-x6',
    vendor: 'Zastone',
    model: 'ZT-X6',
    summary: 'Zastone compact handheld using the RT22 protocol.',
    tags: ['USB', 'Read + write', '16 channels'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    memorySize: 0x0400,
    ranges: RT22_RANGES,
    fileIds: RT22_FILE_IDS,
  },
  {
    protocol: 'rt22',
    id: 'luiton-lt-316',
    vendor: 'LUITON',
    model: 'LT-316',
    summary: 'Luiton compact radio using the RT22 programming protocol.',
    tags: ['USB', 'Read + write', '16 channels'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    memorySize: 0x0400,
    ranges: RT22_RANGES,
    fileIds: RT22_FILE_IDS,
  },
  {
    protocol: 'rt22',
    id: 'tid-td-m8',
    vendor: 'TID',
    model: 'TD-M8',
    summary: 'TID compact radio using the RT22 protocol.',
    tags: ['USB', 'Read + write', '16 channels'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    memorySize: 0x0400,
    ranges: RT22_RANGES,
    fileIds: RT22_FILE_IDS,
  },
  {
    protocol: 'rt22',
    id: 'baofeng-w31e',
    vendor: 'Baofeng',
    model: 'W31E',
    summary: 'Baofeng W31E compact radio with a reduced memory image.',
    tags: ['USB', 'Read + write', '16 channels'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    memorySize: 0x0200,
    ranges: [[0x0000, 0x0200, 0x10]],
    fileIds: RT22_FILE_IDS,
  },
  {
    protocol: 'rt22',
    id: 'baofeng-bf-t20',
    vendor: 'Baofeng',
    model: 'BF-T20',
    summary: 'Baofeng BF-T20 using the RT22 family protocol.',
    tags: ['USB', 'Read + write', '16 channels'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    memorySize: 0x0400,
    ranges: RT22_RANGES,
    fileIds: [
      [0x50, 0x33, 0x33, 0x30, 0x68, 0x33, 0x33],
      [0x50, 0x33, 0x32, 0x30, 0x37, 0x33, 0xf8, 0xff],
    ],
  },
] as const

function decodeKenwoodTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if ((value & 0x2800) === 0x2800) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: Number.parseInt((value & 0x07ff).toString(8).padStart(3, '0'), 10),
      dtcsPolarity: (value & 0x8000) !== 0 ? 'R' : 'N',
    }
  }
  return {
    toneMode: 'ctcss',
    ctcss: (value & 0x7fff) / 10,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

function encodeKenwoodTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const octal = Number.parseInt(channel.dtcs.toString(), 8)
    return octal | 0x2800 | (channel.dtcsPolarity === 'R' ? 0x8000 : 0)
  }
  return 0
}

export function parseRT22Channel(
  image: Uint8Array,
  profile: RT22Profile,
  number: number,
): WebChannel {
  const offset = CHANNEL_OFFSET + (number - profile.channelMinimum) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  const rx = raw.byteLength === CHANNEL_SIZE ? decodeLbcd(raw.slice(0, 4)) : null
  if (!rx) {
    return {
      number,
      empty: true,
      name: '',
      rxFrequency: null,
      txFrequency: null,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
      mode: 'NFM',
      power: 'Low',
      skip: false,
    }
  }
  const tone = decodeKenwoodTone(raw.slice(8, 12))
  const skipByte = image[SKIP_FLAGS_OFFSET + Math.floor((number - 1) / 8)] ?? 0
  return {
    number,
    empty: false,
    name: '',
    rxFrequency: rx,
    txFrequency: decodeLbcd(raw.slice(4, 8)),
    ...tone,
    mode: (raw[13] & 0x10) !== 0 ? 'FM' : 'NFM',
    power: (raw[13] & 0x20) !== 0 ? 'High' : 'Low',
    skip: (skipByte & (1 << ((number - 1) % 8))) === 0,
  }
}

export function parseRT22Channels(
  image: Uint8Array,
  profile: RT22Profile,
): WebChannel[] {
  return Array.from({ length: 16 }, (_, index) =>
    parseRT22Channel(image, profile, index + 1),
  )
}

export function applyRT22Channel(
  image: Uint8Array,
  profile: RT22Profile,
  channel: WebChannel,
): void {
  const offset = CHANNEL_OFFSET + (channel.number - profile.channelMinimum) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  const skipByteIndex = SKIP_FLAGS_OFFSET + Math.floor((channel.number - 1) / 8)
  const bit = 1 << ((channel.number - 1) % 8)
  if (channel.empty) {
    raw.fill(0xff)
    image[skipByteIndex] &= ~bit
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')

  const wasEmpty = decodeLbcd(raw.slice(0, 4)) === null
  const preservedFlags = wasEmpty ? 0 : raw[13] & 0x0f
  raw.fill(0)
  raw[14] = 0x80
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  const tone = encodeKenwoodTone(channel)
  raw[8] = tone & 0xff
  raw[9] = (tone >> 8) & 0xff
  raw.set(raw.slice(8, 10), 10)
  raw[13] =
    preservedFlags |
    (channel.power === 'High' ? 0x20 : 0) |
    (channel.mode === 'FM' ? 0x10 : 0)
  if (channel.skip) {
    image[skipByteIndex] &= ~bit
  } else {
    image[skipByteIndex] |= bit
  }
}

export function normalizeRT22ImageForComparison(image: Uint8Array): Uint8Array {
  const result = image.slice()
  for (let channel = 0; channel < 16; channel += 1) {
    const offset = CHANNEL_OFFSET + channel * CHANNEL_SIZE
    // Firmware rewrites these undocumented padding bytes after an upload.
    result[offset + 14] = 0
    result[offset + 15] = 0
  }
  // The 0x0200-0x033F area contains firmware-generated radio identity and
  // factory/power-on data. The browser programmer neither exposes nor edits
  // this region, and some firmware revisions rewrite it after an upload.
  result.fill(0, 0x0200, 0x0340)
  return result
}

async function enterProgrammingMode(
  profile: RT22Profile,
  transport: RadioTransport,
): Promise<Uint8Array> {
  await transport.discardInput()
  let identified = false
  for (let attempt = 0; attempt < 5; attempt += 1) {
    for (const byte of new TextEncoder().encode('PROGRGS')) {
      await transport.write(Uint8Array.of(byte))
      await new Promise((resolve) => setTimeout(resolve, 5))
    }
    try {
      if ((await transport.readExact(1, 700))[0] === ACK) {
        identified = true
        break
      }
    } catch {
      // Retry the short magic sequence.
    }
  }
  if (!identified) {
    throw new Error(
      'The radio did not enter RT22 programming mode. Check the cable and power-cycle the radio.',
    )
  }

  await transport.write(Uint8Array.of(0x02))
  const ident = await transport.readExact(8, 1200)
  if (
    !profile.fileIds.some((fileId) =>
      Array.from(
        { length: Math.max(0, ident.byteLength - fileId.length + 1) },
        (_, index) => index,
      ).some((index) => fileId.every((value, offset) => ident[index + offset] === value)),
    )
  ) {
    throw new Error('The connected radio did not report a supported RT22-family identity.')
  }
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1200))[0] !== ACK) {
    throw new Error('The radio refused programming mode.')
  }
  await transport.write(Uint8Array.of(0x07))
  if ((await transport.readExact(1, 1200))[0] !== 0x4e) {
    throw new Error('The radio rejected the RT22 session setup.')
  }
  return ident
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
  size: number,
): Promise<Uint8Array> {
  for (const byte of [0x52, (address >> 8) & 0xff, address & 0xff, size]) {
    await transport.write(Uint8Array.of(byte))
    await new Promise((resolve) => setTimeout(resolve, 5))
  }
  const response = await transport.readExact(4 + size, 2000)
  if (
    response[0] !== 0x57 ||
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== size
  ) {
    throw new Error(`Unexpected RT22 block header at 0x${address.toString(16)}.`)
  }
  await new Promise((resolve) => setTimeout(resolve, 5))
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1200))[0] !== ACK) {
    throw new Error(`RT22 did not acknowledge read block 0x${address.toString(16)}.`)
  }
  return response.slice(4)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  for (const byte of [0x57, (address >> 8) & 0xff, address & 0xff, data.byteLength]) {
    await transport.write(Uint8Array.of(byte))
    await new Promise((resolve) => setTimeout(resolve, 5))
  }
  for (const byte of data) {
    await transport.write(Uint8Array.of(byte))
    await new Promise((resolve) => setTimeout(resolve, 5))
  }
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error(`RT22 rejected write block 0x${address.toString(16)}.`)
  }
}

export async function readRT22RadioWithTransport(
  profile: RT22Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const ident = await enterProgrammingMode(profile, transport)
  const image = new Uint8Array(profile.memorySize)
  const total = Math.ceil(profile.memorySize / BLOCK_SIZE)
  for (let offset = 0; offset < profile.memorySize; offset += BLOCK_SIZE) {
    const block = await readBlock(transport, offset, BLOCK_SIZE)
    image.set(block, offset)
    onProgress?.({
      current: offset / BLOCK_SIZE + 1,
      total,
      label: 'Reading RT22 memory',
    })
  }
  await exitProgrammingMode(transport)
  return {
    image,
    reportedModel:
      new TextDecoder('ascii').decode(ident).replace(/[\x00-\x1f\x7f-\xff]/g, '').trim() ||
      profile.model,
  }
}

export async function writeRT22RadioWithTransport(
  profile: RT22Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  const ident = await enterProgrammingMode(profile, transport)
  const imageIdent = image.slice(0x1b8, 0x1c0)
  const patchIdentity =
    imageIdent.byteLength === 8 && imageIdent.every((value) => value === 0x00 || value === 0xff)
  const total = profile.ranges.reduce(
    (count, [start, end, blockSize]) => count + Math.ceil((end - start) / blockSize),
    0,
  )
  let completed = 0
  for (const [start, end, blockSize] of profile.ranges) {
    for (let address = start; address < end; address += blockSize) {
      let block = image.slice(address, address + blockSize)
      if (patchIdentity && address === 0x1b8) {
        block = Uint8Array.from([...ident, ...block.slice(8)])
      }
      await writeBlock(transport, address, block)
      completed += 1
      onProgress?.({ current: completed, total, label: 'Writing RT22 memory' })
    }
  }
  await exitProgrammingMode(transport)
}
